import { useId } from 'react';
import { ToggleButton, ToggleButtonGroup, type Key } from 'react-aria-components';

import styles from './SegmentedControl.module.css';

export interface SegmentedOption {
  id: string;
  label: string;
}

export interface SegmentedControlProps {
  /**
   * The visible lead-in shown before the segments, e.g. "Next:". Also names the group for
   * screen readers. Use `aria-label` instead when there is no visible lead-in.
   */
  label?: string;
  'aria-label'?: string;
  /** Two to five short choices. For more, use a Select. */
  options: readonly SegmentedOption[];
  /** The id of the selected option. One is always selected. */
  value: string;
  onChange: (id: string) => void;
  isDisabled?: boolean;
}

/**
 * A small set of mutually exclusive views, like "7 days | 14 days | 30 days" (ADR 0019):
 * a light grey track with the selected option as a white pill. It sits at the right of a
 * card's title row (Section `actions`). Arrow keys move between options.
 */
export function SegmentedControl({
  label,
  options,
  value,
  onChange,
  isDisabled = false,
  ...aria
}: SegmentedControlProps) {
  const labelId = useId();
  return (
    <div className={styles.wrap}>
      {label === undefined ? null : (
        <span id={labelId} className={styles.label}>
          {label}
        </span>
      )}
      <ToggleButtonGroup
        className={styles.track}
        selectionMode="single"
        disallowEmptySelection
        selectedKeys={[value]}
        isDisabled={isDisabled}
        onSelectionChange={(keys: Set<Key>) => {
          const [first] = [...keys];
          if (first !== undefined && String(first) !== value) onChange(String(first));
        }}
        {...(label !== undefined
          ? { 'aria-labelledby': labelId }
          : aria['aria-label'] !== undefined
            ? { 'aria-label': aria['aria-label'] }
            : {})}
      >
        {options.map((option) => (
          <ToggleButton key={option.id} id={option.id} className={styles.segment}>
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </div>
  );
}
